import { VariantProps, cva } from "class-variance-authority";
import { format } from "date-fns";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import { ChatMessage } from "../types/chat-message";

const messageBodyVariants = cva("text-sm rounded-md p-2 border break-words", {
  variants: {
    variant: {
      currentUser:
        "bg-primary text-primary-foreground rounded-tr-none border-primary",
      otherUser: "text-muted-foreground bg-card rounded-tl-none border-border",
    },
  },
});

export interface MessageBodyProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof messageBodyVariants> {
  text: string;
}

function MessageBody({ text, variant, className }: MessageBodyProps) {
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = messageRef.current;
    if (!element) return;

    // Find URLs in text
    const urlRegex = /(https?:\/\/\S+)/g;
    const matches = text.match(urlRegex);

    if (!matches) return;

    // For each URL found
    matches.forEach((url) => {
      // Create link element
      const link = document.createElement("a");
      link.href = url;
      link.textContent = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.className = "text-foreground underline underline-offset-4";

      // Replace text node containing URL with link
      const nodes = element.childNodes;
      nodes.forEach((node) => {
        if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent?.includes(url)
        ) {
          const parts = node.textContent.split(url);
          const fragment = document.createDocumentFragment();

          if (parts[0]) fragment.appendChild(document.createTextNode(parts[0]));
          fragment.appendChild(link.cloneNode(true));
          if (parts[1]) fragment.appendChild(document.createTextNode(parts[1]));

          node.parentNode?.replaceChild(fragment, node);
        }
      });
    });
  }, [text]);

  return (
    <div
      ref={messageRef}
      className={cn(messageBodyVariants({ variant }), className)}
    >
      {text}
    </div>
  );
}

export function Message({ message }: { message: ChatMessage }) {
  const isCurrentUser = message.participantId === "You";
  return (
    <div
      className={cn("flex w-[90%] flex-col gap-1", {
        "ml-auto": isCurrentUser,
        "mr-auto": !isCurrentUser,
      })}
    >
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-secondary" data-sentry-mask>
          {message.participantName}
        </p>
        <p className="ml-auto text-xs text-muted" data-sentry-mask>
          {format(message.timestamp, "HH:mm")}
        </p>
      </div>
      <MessageBody
        text={message.message}
        variant={isCurrentUser ? "currentUser" : "otherUser"}
      />
    </div>
  );
}
